import { Analytics } from '@plaudit/analytics';
const analytics = Analytics.getInstance();

import isInView from "../lib/isInView";
import passiveIfSupported from "../lib/passiveIfSupported";
import Debouncer from '../lib/Debouncer';
import * as $ from "jquery";
import InfoWindow = google.maps.InfoWindow;
const MarkerClusterer = require('@googlemaps/markerclustererplus');
declare function require(name: string): any;
const mapElement = document.getElementById('map');
const listingElement = document.getElementById('locationListingResults');

type MapControllerState = {
	map: google.maps.Map,
	markers: google.maps.Marker[],
	baseCenter: google.maps.LatLng,
	infoWindows: google.maps.InfoWindow[],
	thingsToClear: any[],
	fresh: boolean,
	reset: () => void,
	baseBounds: google.maps.LatLngBounds,
	unshiftedBounds: google.maps.LatLngBounds
};
type AdditionalMapControllerOptions = {
	autoRefreshBounds?: boolean,
	markerIconRefresher?: (state: MapControllerState) => (marker: google.maps.Marker) => void,
	boundsApplier?: (state: MapControllerState) => void
};

type MarkerProvider = (state: MapControllerState, panBack: () => void) => google.maps.Marker[];

type InfoWindowListenerMaker = (state: MapControllerState, marker: google.maps.Marker, contentString: string, panBack: () => void) => (() => google.maps.InfoWindow);

type BoundsComputer = (state: MapControllerState) => google.maps.LatLngBounds;

const DEFAULT_BOUNDS_COMPUTER: BoundsComputer = state => {
	const bounds = new google.maps.LatLngBounds();
	state.markers.forEach(marker => bounds.extend(marker.getPosition()));
	return bounds;
};

(window as any).googleMapScriptLoaded = () => { };

const pinSizes = [5, 7.5, 10, 15, 25, 27.5, 30, 35, 25, 25, 25, 25, 25, 25, 25, 35, 35, 35, 35, 35, 35, 35, 35, 35, 35];
const updateMarkerSize = (state: MapControllerState) => (marker: google.maps.Marker) => {
	let scale: any = state.map.getZoom()
	marker.setIcon({
		url: (marker.getIcon() as any).url,
		anchor: new google.maps.Point(pinSizes[scale] / 2, pinSizes[scale] / 2),
		scaledSize: new google.maps.Size(pinSizes[scale], pinSizes[scale])
	}
	);
};
const updateMarkerSizes = (state: MapControllerState) => {
	state.markers.forEach(updateMarkerSize(state));
};
const getFixedPositionPadding = () => {
	let padding: google.maps.Padding = {
		top: (document.getElementById('filterForm').getBoundingClientRect().height) + 20,
		right: 20,
		bottom: 40,
		left: (listingElement.getBoundingClientRect().right) + 20
	};
	return padding;
};
const mapController = (
	applicabilityTest: () => boolean,
	markerProvider: MarkerProvider,
	boundsComputer: BoundsComputer = DEFAULT_BOUNDS_COMPUTER,
	options: AdditionalMapControllerOptions = {}
) => {
	if (!applicabilityTest()) {
		return {};
	}
	if (!options) {
		options = {};
	}
	let firstRun = true;

	const state: MapControllerState = {
		map: null,
		markers: null,
		baseCenter: null,
		infoWindows: [],
		thingsToClear: [],
		fresh: true,
		reset: function () {
			closeInfoWindows(this.infoWindows, true);
			clearMarkers(this.markers);
			this.map = null;
			this.markers = null;
			this.baseCenter = null;
			this.unshiftedBounds = null;
			this.infoWindows = [];
			if (!!this.thingsToClear) {
				this.thingsToClear.forEach((thingToClear: any) => {
					thingToClear.setMap(null);
				});
			}
			this.thingsToClear = [];
			state.fresh = true;
		},
		baseBounds: null,
		unshiftedBounds: null
	};
	const panBack = () => {
		if (state.baseCenter !== null && state.infoWindows.length === 0) {
			state.map.panTo(state.baseCenter);
			state.baseCenter = null;
			state.unshiftedBounds = null;
		}
	};
	const initMap = () => {
		if (firstRun) {
			document.addEventListener('jsfAjaxEvent', evt => {
				if ((evt as any).detail.fireType === 'always') {
					if (document.body.querySelector('#map:not(.map-set-up)') !== null) {
						state.reset();
						initMap();
					} else if (!!document.body.querySelector('.map-needs-updating')) {
						closeInfoWindows(state.infoWindows, true);
						clearMarkers(state.markers);
						panBack();
						initMap();
					}
				}
			});
			(window as any).refreshMap = () => {
				closeInfoWindows(state.infoWindows, true);
				clearMarkers(state.markers);
				panBack();
				initMap();
			}
			(window as any).refreshMarkerIcons = !options.markerIconRefresher
				? () => { }
				: () => {
					preloadMapMarkerOptions();
					state.markers.forEach(options.markerIconRefresher(state));
				};
			firstRun = false;
		}
		const mapNeedsUpdatingIndicator = document.body.querySelector('.map-needs-updating');
		if (!!mapNeedsUpdatingIndicator) {
			mapNeedsUpdatingIndicator.classList.remove('map-needs-updating');
		}

		clearMarkers(state.markers);

		if (state.map === null) {
			mapElement.classList.add('map-set-up'); // This is used to account for AJAX requests that replace the map

			state.map = new google.maps.Map(mapElement, {
				zoom: 5,
				maxZoom: 21,
				minZoom: 3,
				fullscreenControl: false,
				mapTypeControl: false,
				streetViewControl: false,
				zoomControl: true
			});
		}

		preloadMapMarkerOptions();
		state.markers = markerProvider(state, panBack);

		const updatingBounds = state.baseBounds === null || options.autoRefreshBounds !== false;
		if (updatingBounds) {
			const bounds = boundsComputer(state);
			state.markers.forEach(marker => marker.setMap(state.map));
			state.map.setCenter(bounds.getCenter());

			const adjustPosition = document.defaultView.getComputedStyle(listingElement, "").getPropertyValue('position') === 'absolute';
			if (adjustPosition) {
				state.map.fitBounds(bounds, getFixedPositionPadding());
			} else {
				state.map.fitBounds(bounds, {});
			}
			if (state.map.getZoom() > 21) state.map.setZoom(21);
			state.baseBounds = bounds;
		} else {
			state.markers.forEach(marker => marker.setMap(state.map));
		}
		// This listener ensures that the map's zoom is set correctly.
		const listener = google.maps.event.addListener(state.map, "idle", () => {
			if (state.map.getZoom() > 21) state.map.setZoom(21);
			google.maps.event.removeListener(listener);
			state.fresh = false;
		});
		google.maps.event.addListener(state.map, "zoom_changed", () => updateMarkerSizes(state));
		if (!!options.boundsApplier) {
			const boundsApplicationDebouncer = new Debouncer(() => {
				closeInfoWindows(state.infoWindows, true);
				panBack();
				options.boundsApplier(state);
			});
			google.maps.event.addListener(state.map, "zoom_changed", boundsApplicationDebouncer.run);
			google.maps.event.addListener(state.map, "bounds_changed", boundsApplicationDebouncer.run);
			google.maps.event.addListener(state.map, "resize", boundsApplicationDebouncer.run);
		}
	};
	const initMapIfReady = () => {
		if (isGoogleMapScriptLoaded && hasMapBeenInView) {
			initMap();
		}
	};

	let isGoogleMapScriptLoaded = false;
	(window as any).googleMapScriptLoaded = () => {
		isGoogleMapScriptLoaded = true;
		initMapIfReady();
	};

	let hasMapBeenInView = false;
	const mapIsInView = () => {
		hasMapBeenInView = true;
		initMapIfReady();
	};
	const onScroll = () => {
		const _isInView = isInView(mapElement);
		if (_isInView === true) {
			window.removeEventListener("scroll", debouncedOnScroll.run, passiveIfSupported);
			window.removeEventListener("resize", debouncedOnScroll.run, passiveIfSupported);
			mapIsInView();
		}
	};
	const debouncedOnScroll = new Debouncer(onScroll);
	window.addEventListener("scroll", debouncedOnScroll.run, passiveIfSupported);
	window.addEventListener("resize", debouncedOnScroll.run, passiveIfSupported);
	debouncedOnScroll.runNow();

	// set event listeners
	document.addEventListener('click', function (event) {
		let parent: HTMLElement = event.target as HTMLElement;
		do {
			if (parent.style.zIndex === '202') {
				event.stopPropagation();
				return;
			}
		} while ((parent = (parent.parentElement ?? (parent.parentNode as HTMLElement))) !== null && (parent.id === null || parent.id === ''));

		closeInfoWindows(state.infoWindows, false);
		panBack();
	});
	document.addEventListener('keyup', function (event) {
		if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
			closeInfoWindows(state.infoWindows, true);
			panBack();
		}
	});
};
const setBaseCenterIfNeeded = (state: MapControllerState, baseCenter: google.maps.LatLng) => {
	if (state.baseCenter === null) {
		state.baseCenter = baseCenter;
		state.unshiftedBounds = state.map.getBounds();
	}
};
const clearMarkers = (markers: google.maps.Marker[]) => {
	if (!!markers) {
		markers.forEach(marker => {
			const onRemoveAction: () => void = marker.get('onRemoveAction');
			marker.setMap(null);
			if (typeof onRemoveAction === 'function') {
				onRemoveAction();
			}
		});
	}
};
const closeInfoWindows = (infoWindows: google.maps.InfoWindow[], ignoreCanBeRemoved: boolean) => {
	if (!!infoWindows) {
		let min = 0;
		while (infoWindows.length > min) {
			if (ignoreCanBeRemoved || (infoWindows[infoWindows.length - 1] as any).canBeRemoved) {
				const infoWindow = infoWindows.pop();
				(infoWindow.get('builtInOnClose') as (() => void))();
				const onCloseAction: () => void = infoWindow.get('onCloseAction');
				infoWindow.close();
				if (typeof onCloseAction === 'function') {
					onCloseAction();
				}
			} else {
				min++;
				(infoWindows[infoWindows.length - 1] as any).canBeRemoved = true;
			}
		}
	}
};
const makeInfoWindowListener: InfoWindowListenerMaker = (state, marker, contentString, panBack) => {
	return function () {
		setBaseCenterIfNeeded(state, state.map.getCenter());
		closeInfoWindows(state.infoWindows, true);
		const infoWindow = new google.maps.InfoWindow({
			content: contentString,
			zIndex: 202
		});
		infoWindow.open(state.map, marker);
		marker.set('selected', true);
		marker.setIcon(mapMarkerOptions[(marker.get('markerType') ?? 'default') + '-selected'].icon);
		updateMarkerSize(state)(marker);
		(infoWindow as any).canBeRemoved = false;
		state.infoWindows.push(infoWindow);
		infoWindow.set('builtInOnClose', () => {
			marker.set('selected', undefined);
			marker.setIcon(mapMarkerOptions[(marker.get('markerType') ?? 'default')].icon);
			updateMarkerSize(state)(marker);
		})
		infoWindow.addListener('closeclick', () => {
			closeInfoWindows(state.infoWindows, true);
			panBack();
		});
		return infoWindow
	};
};
const mapMarkerOptions: any = {
	'default': { zIndex: 0, fill: '000000' },
	'default-selected': { zIndex: 1, fill: 'B78C1F' },
	'default-hovered': { zIndex: 1, fill: 'B78C1F' },
	'default-selected-hovered': { zIndex: 1, fill: 'B78C1F' }
};
const preloadMapMarkerOptions = () => {
	for (let mapMarkerOptionsKey in mapMarkerOptions) {
		if (mapMarkerOptions.hasOwnProperty(mapMarkerOptionsKey)) {
			const data = mapMarkerOptions[mapMarkerOptionsKey];
			if (!data['icon']) {
				data['icon'] = {
					url: 'data:image/svg+xml;base64, ' + btoa('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 38" fill="#' + data['fill'] + '">' +
						'<path d="M14.1 18.5c-2.7 0-4.9-2.2-4.9-4.9s2.2-4.9 4.9-4.9 4.9 2.2 4.9 4.9c-.1 2.7-2.2 4.9-4.9 4.9zm0-18.5C6.6 0 .5 6 .4 13.5v.2C.4 21.9 9 33 12.5 37.3c.6.8 1.8 1 2.6.4.1-.1.3-.2.4-.4C19 33 27.6 21.9 27.6 13.7 27.8 6.3 22 .2 14.6 0h-.5z"> </path>' +
						'</svg>'),
					size: new google.maps.Size(28, 38),
					scaledSize: new google.maps.Size(28, 38),
				}
			}
		}
	}
}
const addMarker = (
	state: MapControllerState,
	newMarkers: google.maps.Marker[],
	markerType: string,
	lat: any,
	lon: any,
	contentString: string,
	panBack: () => void,
	onClickListenerProducer: (marker: google.maps.Marker) => () => void,
	infoWindowListenerMaker: InfoWindowListenerMaker = makeInfoWindowListener
): google.maps.Marker => {
	const marker = new google.maps.Marker({
		position: new google.maps.LatLng(Number(lat), Number(lon)),
		icon: (mapMarkerOptions[markerType] ?? mapMarkerOptions['default']).icon,
		zIndex: (mapMarkerOptions[markerType] ?? mapMarkerOptions['default']).zIndex,
		optimized: false
	});
	marker.set('markerType', markerType);
	if (infoWindowListenerMaker !== null) {
		marker.addListener('click', infoWindowListenerMaker(state, marker, contentString, panBack));
	}
	if (onClickListenerProducer !== null) {
		marker.addListener('click', onClickListenerProducer(marker));
	}
	newMarkers.push(marker);
	return marker;
};

const mapControllerInitializer = () => {
	let geocoder: google.maps.Geocoder = null;
	const markerTypeDeterminer: (element: Element) => string = element => {
		let markerType = (element.getAttribute('data-marker-type') ?? 'default').toLowerCase();
		if (element.getAttribute('data-property-is-saved') === 'true') {
			markerType += '-saved';
		}
		if (element.getAttribute('data-property-is-compared') === 'true') {
			markerType += '-compared';
		}
		return markerType;
	};
	let mapMarkerClusterer: MarkerClusterer = null;

	const doGeocoding = (element: HTMLInputElement) => {
		if (element.value.length !== 0) {
			let foundBounds: google.maps.LatLngBounds = null;
			if (!!(window as any).propertyListingMapState && !!(window as any).propertyListingMapState.map) {
				foundBounds = (window as any).propertyListingMapState.map.getBounds();
			}
			geocoder.geocode({ 'address': element.value, 'bounds': foundBounds },
				(results, status) => {
					if (status == 'OK') {
						element.setAttribute("data-result-usable", "true");
						(window as any).geocoderResult = results[0];
						if (results[0].geometry.bounds === null || results[0].geometry.bounds === undefined) {
							results[0].geometry.bounds = foundBounds;
						}
					} else {
						element.setAttribute("data-result-usable", "false");
						(window as any).geocoderResult = null;
					}
					(window as any).refreshMap();
				});
		} else {
			element.setAttribute("data-result-usable", "false");
			(window as any).geocoderResult = null;
		}
		updateQueryParameters();
	}
	const selectedTypes: string[] = [];

	const updateQueryParameters = () => {
		if (!!window.URLSearchParams) { // If you're on IE11, I feel bad for you.
			const params = new URLSearchParams(window.location.search);
			const location = (document.querySelector('input.location') as HTMLInputElement).value;
			if (location === null || location === undefined || location.length === 0) {
				params.delete("location");
			} else {
				params.set("location", location);
			}
			params.delete("locationType");
			for (let i = 0; i < selectedTypes.length; i++) {
				params.append("locationType", selectedTypes[i]);
			}
			params.sort();
			history.replaceState({}, "", window.location.pathname + '?' + params.toString());
				/*GA*/analytics.trackEvent('Map', 'Search Parameters', (params.get('location') ?? '') + '||' + (params.getAll('locationType') ?? []).join(','));
		}
	}

		mapController(() => {
			if (document.querySelector('.locationType') !== null) {
				const allCheckboxes: NodeListOf<HTMLInputElement> = document.querySelectorAll('.locationType input[type="checkbox"]');
				for (let i = 0; i < allCheckboxes.length; i++) {
					if (allCheckboxes.item(i).checked) {
						selectedTypes.push(allCheckboxes.item(i).value);
					}
				}
				$(document).on('change', '.locationType input[type="checkbox"]', (event) => {
					const target = event.target;
					const changedValue = (target as HTMLInputElement).value;
					if ((target as HTMLInputElement).checked) {
						selectedTypes.push(changedValue);
					} else {
						for (let i = 0; i < selectedTypes.length; i++) {
							if (selectedTypes[i] === changedValue) {
								selectedTypes.splice(i, 1);
							}
						}
					}
					if (typeof (window as any).refreshMap === "function") {
						(window as any).refreshMap();
					}
					updateQueryParameters();
				});
				$(document).on('submit', 'form.locationListing__filterForm', event => {
					doGeocoding((event.target as HTMLFormElement).querySelector('input.location') as HTMLInputElement);
					event.preventDefault();
				});
				// $(document).on('change', 'input.location', event => doGeocoding(event.target as HTMLInputElement));

				return true;
			}
			return false;
		}, (state, panBack) => {

			if (geocoder === null) {
				geocoder = new google.maps.Geocoder();
			}
			(window as any).propertyListingMapState = state;
			if (!mapMarkerClusterer || mapMarkerClusterer.getMap() !== state.map) {
				const markerClustererStyles = {
					gridSize: 26,
					styles: [{
						url: 'assets/images/design/pin-group.svg',
						height: 26,
						width: 26,
						textColor: '#ffffff',
						textSize: 10
					}]
				};
				mapMarkerClusterer = new MarkerClusterer(state.map, [], markerClustererStyles);
				mapMarkerClusterer.setMaxZoom(18);

				google.maps.event.addListener(mapMarkerClusterer, "click", (event) => {
					const adjustPosition = document.defaultView.getComputedStyle(listingElement, "").getPropertyValue('position') === 'absolute';
					let padding = (adjustPosition) ? getFixedPositionPadding() : null;

					// NOTE: This is a recreation of the default click handler
					const mc = event.getMarkerClusterer();
					const mz = mc.getMaxZoom();
					const theBounds = event.clusterIcon_.cluster_.getBounds();
					(mc.getMap() as google.maps.Map).fitBounds(theBounds, padding);
					setTimeout(() => {
						(mc.getMap() as google.maps.Map).fitBounds(theBounds, padding);
						// Don't zoom beyond the max zoom level
						if (mz !== null && mc.getMap().getZoom() > mz) {
							mc.getMap().setZoom(mz + 1);
						}
					}, 100);
				});
			} else {
				mapMarkerClusterer.clearMarkers();
			}

			const results: NodeListOf<HTMLElement> = document.querySelectorAll('.locationListing__items .listingItem');
			let newMarkers: google.maps.Marker[] = [];

			let lastClickedMarker: google.maps.Marker = null;
			const infoWindowListenerMaker: InfoWindowListenerMaker = (
				state: MapControllerState,
				marker: google.maps.Marker,
				contentString: string,
				panBack: () => void
			) => {
				const madeInfoWindowListener: () => InfoWindow = makeInfoWindowListener(state, marker, contentString, panBack);
				return () => {
					if (lastClickedMarker !== null && lastClickedMarker !== marker) {
						lastClickedMarker.get('sourceElement').classList.remove('isMarkerActive');
					}
					lastClickedMarker = marker;
					let markerElement = marker.get('sourceElement');
					markerElement.classList.add('isMarkerActive');
					markerElement.parentElement.scrollTop = markerElement.offsetTop;

						/*GA*/analytics.trackEvent('Map', 'Marker Click', markerElement.getAttribute('data-location-id'));

					const infoWindow = madeInfoWindowListener();
					infoWindow.set('onCloseAction', () => {
						if (lastClickedMarker === marker) {
							lastClickedMarker.get('sourceElement').classList.remove('isMarkerActive');
							lastClickedMarker = null;
						}
					});
					return infoWindow;
				}
			}

			for (let i = 0; i < results.length; i++) {
				const result = results.item(i);
				let included = false;
				if (selectedTypes.length === 0) { // If no types are selected, treat it as unfiltered
					included = true;
				} else {
					const locationTypes = result.getAttribute('data-location-type').split(',');
					for (let j = 0; j < locationTypes.length; j++) {
						if (selectedTypes.indexOf(locationTypes[j]) > -1) {
							included = true;
							break;
						}
					}
				}
				if (included === false) {
					result.style.display = 'none';
					continue;
				}
				const propertyId = result.getAttribute('data-location-id');
				const heading = result.querySelector('.address__addressee');
				const contentString =
					'<div class="mapToolTip" data-location-id="' + propertyId + '">' +
					'<div>' +
					'<div class="mapToolTip__heading">' + heading.textContent + '</div>' +
					'</div>' +
					'</div>';
				if (result.getAttribute('data-lat') !== null && result.getAttribute('data-lon') !== null) {
					const markerType = markerTypeDeterminer(result);
					const marker = addMarker(
						state, newMarkers, markerType,
						result.getAttribute('data-lat'),
						result.getAttribute('data-lon'),
						contentString, panBack, null, infoWindowListenerMaker
					);

					marker.set('destination', heading);
					mapMarkerClusterer.addMarker(marker);

					marker.set('sourceElement', result);
					const onOver = () => {
						result.classList.add('isMarkerHover');
						marker.setIcon(mapMarkerOptions[(marker.get('markerType') ?? 'default') + (!!marker.get('selected') ? '-selected' : '-hovered')].icon);
						updateMarkerSize(state)(marker);
					};
					const onOut = () => {
						result.classList.remove('isMarkerHover');
						result.classList.remove('isMouseDown');
						marker.setIcon(mapMarkerOptions[(marker.get('markerType') ?? 'default') + (!!marker.get('selected') ? '-selected' : '')].icon);
						updateMarkerSize(state)(marker);
					};
					const onMouseDown = () => {
						result.classList.add('isMouseDown');
					};
					const onMouseUp = () => {
						result.classList.remove('isMouseDown');
					};
					marker.addListener('mouseover', onOver);
					marker.addListener('mouseout', onOut);
					result.addEventListener('mouseover', onOver);
					result.addEventListener('mouseout', onOut);
					result.addEventListener('mousedown', onMouseDown);
					result.addEventListener('mouseup', onMouseUp);

					marker.set('onRemoveAction', () => {
						onOut();
						result.removeEventListener('mouseover', onOver);
						result.removeEventListener('mouseout', onOut);
						result.removeEventListener('mousedown', onMouseDown);
						result.removeEventListener('mouseup', onMouseUp);
					});
				}
			}
			return newMarkers;
		}, state => {
			const bounds = new google.maps.LatLngBounds();
			if (state.fresh && state.thingsToClear.length > 0) {
				state.thingsToClear.forEach(ttc => bounds.union(ttc.getBounds()));
			} else {
				if (state.fresh !== false) {
					doGeocoding(document.querySelector('input.location'));
				}
				const locationBox = document.querySelector('input.location[data-result-usable="true"]');
				if (locationBox !== null) {
					const geocoderResult: google.maps.GeocoderResult = (window as any).geocoderResult;

					const geocodedBounds = geocoderResult.geometry.bounds;
					let closest = state.markers[0];
					if (geocodedBounds.contains(closest.getPosition())) {
						return geocodedBounds;
					}
					let closestDistance = Math.sqrt(Math.pow(geocodedBounds.getCenter().lat() - closest.getPosition().lat(), 2)
						+ Math.pow(geocodedBounds.getCenter().lng() - closest.getPosition().lng(), 2));
					for (let i = 1; i < state.markers.length; i++) {
						if (geocodedBounds.contains(state.markers[i].getPosition())) {
							return geocodedBounds;
						}
						let currentDistance = Math.sqrt(Math.pow(geocodedBounds.getCenter().lat() - state.markers[i].getPosition().lat(), 2)
							+ Math.pow(geocodedBounds.getCenter().lng() - state.markers[i].getPosition().lng(), 2));
						if (currentDistance < closestDistance) {
							closest = state.markers[i];
							closestDistance = currentDistance;
						}
					}

					geocodedBounds.extend({
						lat: closest.getPosition().lat() + Math.sign(closest.getPosition().lat() - geocodedBounds.getCenter().lat()),
						lng: closest.getPosition().lng() + Math.sign(closest.getPosition().lng() - geocodedBounds.getCenter().lng())
					});
					return geocodedBounds;
				} else {
					return DEFAULT_BOUNDS_COMPUTER(state);
				}
			}
			return bounds;
		}, {
			markerIconRefresher: state => marker => {
				const sourceElement: HTMLElement = marker.get('sourceElement');
				if (sourceElement !== null) {
					const markerType = markerTypeDeterminer(sourceElement);
					marker.set('markerType', markerType);
					if (!!marker.get('selected')) {
						marker.setIcon(mapMarkerOptions[(markerType || 'default') + '-selected'].icon);
					} else {
						if (sourceElement.classList.contains('isMarkerHover')) {
							marker.setIcon(mapMarkerOptions[(markerType || 'default') + '-hovered'].icon);
						} else {
							marker.setIcon(mapMarkerOptions[(markerType || 'default')].icon);
						}
					}
					updateMarkerSize(state)(marker);
				}
			},
			boundsApplier: state => {
				const bounds = state.unshiftedBounds ?? state.map.getBounds();
				// Update the listing on the side to only display items in view
				for (let i = 0; i < state.markers.length; i++) {
					let marker = state.markers[i];
					marker.get('sourceElement').style.display = bounds.contains(marker.getPosition()) ? '' : 'none';
				}
			}
		});
	// IE11 safe events
	const detailsLinks: NodeList = listingElement.querySelectorAll('.listingItem__detailsLink a');
	for (let i = 0, l = detailsLinks.length; i < l; ++i) {
		detailsLinks[i].addEventListener('click', function (event) {
			event.preventDefault();
			document.getElementById(this.href.split('#')[1]).setAttribute('data-target', 'true');
				/*GA*/analytics.trackEvent('Map', 'Location Details Click', this.href.split('#')[1]);
		});
	}
	const itemLinks: NodeList = listingElement.querySelectorAll('.listingDetail__itemLink a');
	for (let i = 0, l = itemLinks.length; i < l; ++i) {
		itemLinks[i].addEventListener('click', function (event) {
			event.preventDefault();
			this.closest('[data-target="true"]').setAttribute('data-target', '');
		});
	}
};

export default mapControllerInitializer;
